import React, { useState, useCallback } from 'react';
import { Toast, ToastProps } from '../components/Toast';
import { ToastContext, ToastContextType } from './ToastContext';

interface ToastItem extends ToastProps {
  id: string;
}

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const [isHovered, setIsHovered] = useState(false);

  const removeToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const showToast = useCallback(
    (toast: Omit<ToastProps, 'id' | 'onClose'>) => {
      const id =
        Date.now().toString() + Math.random().toString(36).substr(2, 9);
      const newToast: ToastItem = {
        ...toast,
        id,
        onClose: removeToast,
      };
      setToasts(prev => [...prev, newToast]);
    },
    [removeToast]
  );

  const showError = useCallback(
    (title: string, message?: string) => {
      // Error toast should NOT auto-dismiss (duration = 0)
      showToast({ type: 'error', title, message, duration: 0 });
    },
    [showToast]
  );

  const showWarning = useCallback(
    (title: string, message?: string) => {
      // Other toast types disappear after 5 seconds (5000ms)
      showToast({ type: 'warning', title, message, duration: 5000 });
    },
    [showToast]
  );

  const showSuccess = useCallback(
    (title: string, message?: string) => {
      // Other toast types disappear after 5 seconds (5000ms)
      showToast({ type: 'success', title, message, duration: 5000 });
    },
    [showToast]
  );

  const showInfo = useCallback(
    (title: string, message?: string) => {
      // Other toast types disappear after 5 seconds (5000ms)
      showToast({ type: 'info', title, message, duration: 5000 });
    },
    [showToast]
  );

  const contextValue: ToastContextType = React.useMemo(() => ({
    showToast,
    showError,
    showWarning,
    showSuccess,
    showInfo,
  }), [showToast, showError, showWarning, showSuccess, showInfo]);

  // Dynamic height of the expanded container to prevent overflow issues
  const containerHeight = toasts.length > 0
    ? isHovered 
      ? `${toasts.length * 78}px` 
      : '70px'
    : '0px';

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      {/* Stacked Toasts Container */}
      {toasts.length > 0 && (
        <div 
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          style={{
            position: 'fixed',
            top: '100px',
            right: '24px',
            zIndex: 99999,
            width: '360px',
            height: containerHeight,
            transition: 'height 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.1)',
            pointerEvents: 'auto',
          }}
        >
          {toasts.map((toast, index) => {
            const total = toasts.length;
            const stackIndex = total - 1 - index;
            return (
              <Toast 
                key={toast.id} 
                {...toast} 
                stackIndex={stackIndex}
                total={total}
                isHovered={isHovered}
              />
            );
          })}
        </div>
      )}
    </ToastContext.Provider>
  );
};
